import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SharedServiceService } from '../../services/shared-service.service';

export interface NeoTranscriptEntry {
  role: string;
  message?: string;
  originalMessage?: string;
  timeInCallSecs: number;
  interrupted: boolean;
  sourceEventId: number;
}

export interface NeoConversation {
  conversationId: string;
  onboardingStatus: string;
  conversationSummary: string;
  timestamp: number;
  transcript: NeoTranscriptEntry[];
}

export interface MemberTranscriptResponse {
  success: boolean;
  memberId: string;
  transcript: string;
  isEnabled: boolean;
  isArchived: boolean;
  timeZone: string;
  neoConversations: NeoConversation[];
  message?: string;
}

export interface ApiActionResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class MemberTranscriptApiService {
  constructor(
    private http: HttpClient,
    private sharedService: SharedServiceService
  ) {}

  getMemberTranscript(memberId: string): Observable<MemberTranscriptResponse> {
    const headers = this.createRenneApiHeaders();
    const params = new HttpParams().set('memberId', memberId);
    return this.http.get<MemberTranscriptResponse>(`${this.getBaseUrl()}member`, {
      headers,
      params,
    });
  }

  updateMemberStatus(
    memberId: string,
    isEnabled: boolean
  ): Observable<ApiActionResponse> {
    const headers = this.createRenneApiHeaders();
    const params = new HttpParams().set('memberId', memberId);
    return this.http.put<ApiActionResponse>(
      `${this.getBaseUrl()}member/status`,
      { isEnabled },
      { headers, params }
    );
  }

  archiveMember(memberId: string): Observable<ApiActionResponse> {
    const headers = this.createRenneApiHeaders();
    return this.http.patch<ApiActionResponse>(
      `${this.getBaseUrl()}member/${memberId}/archive`,
      {},
      { headers }
    );
  }

  unarchiveMember(memberId: string): Observable<ApiActionResponse> {
    const headers = this.createRenneApiHeaders();
    return this.http.patch<ApiActionResponse>(
      `${this.getBaseUrl()}member/${memberId}/unarchive`,
      {},
      { headers }
    );
  }

  autofillFromTranscript(
    memberId: string,
    transcript: string,
    questions: any[]
  ): Observable<any> {
    const headers = this.createRenneApiHeaders();
    return this.http.post<any>(
      `${this.getBaseUrl()}member/${memberId}/transcript/autofill`,
      { transcript, questions },
      { headers }
    );
  }

  private getBaseUrl(): string {
    const rawBase = environment.apiBaseUrl ?? '';
    const trimmed = rawBase.trim().replace(/\/+$/, '');
    return `${trimmed}/`;
  }

  /** Bearer JWT from Dhruva login + Renne X-ApiAccess-Key from appsettings. */
  private createRenneApiHeaders(): HttpHeaders {
    const token = this.sharedService.getToken();
    const apiKey =
      environment.apiKey ?? environment.apiKey;
    return new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'X-ApiAccess-Key': apiKey,
    });
  }
}
